import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api, { assignmentAPI, blockedDatesAPI, driveAPI, personalGearAPI, crewAPI } from '../utils/api';
import { formatDate, formatCurrency } from '../utils/helpers';
import { toast } from 'sonner';
import { Calendar, CalendarPlus, MapPin, Clock, DollarSign, CheckCircle, AlertCircle, Check, XCircle, CalendarOff, FileText, Image, Film, File as FileIcon, ExternalLink, FolderOpen, Zap, X, Eye, EyeOff, Package, Plus, Trash2, RotateCcw, ShieldCheck, ClipboardCheck, Camera } from 'lucide-react';
import ShotListCrew from '../components/ShotListCrew';
import { Avatar } from '../components/Avatar';

const CrewDashboard = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('weekly');
  const [mainTab, setMainTab] = useState('schedule');
  const [projectFiles, setProjectFiles] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [myRates, setMyRates] = useState(null);
  const [showRates, setShowRates] = useState(false);
  // Personal Gear state
  const [personalGear, setPersonalGear] = useState([]);
  const [newGearItem, setNewGearItem] = useState('');
  const [gearLoading, setGearLoading] = useState(false);

  // Generate Google Calendar URL for adding event
  const generateCalendarUrl = (item) => {
    const event = item.event;
    const eventDate = event.event_date.replace(/-/g, '');
    
    // Parse start and end times
    const parseTime = (timeStr) => {
      const [time, period] = timeStr.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return `${hours.toString().padStart(2, '0')}${(minutes || 0).toString().padStart(2, '0')}00`;
    };
    
    const startTime = parseTime(event.start_time);
    const endTime = parseTime(event.end_time);
    
    const title = encodeURIComponent(`Ami Video - ${event.event_name}${item.bride_name && item.groom_name ? ` - ${item.bride_name} & ${item.groom_name}` : ''}`);
    const location = encodeURIComponent(event.venue_address || event.location || '');
    const details = encodeURIComponent(`Event Type: ${event.event_type}\n${event.event_notes || ''}`);
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${eventDate}T${startTime}/${eventDate}T${endTime}&details=${details}&location=${location}`;
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return FileIcon;
    if (mimeType.startsWith('image/')) return Image;
    if (mimeType.startsWith('video/')) return Film;
    if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text')) return FileText;
    return FileIcon;
  };

  const fetchProjectFiles = async (projectId) => {
    if (projectFiles[projectId]) return; // Already fetched
    try {
      const res = await driveAPI.listFiles(projectId);
      setProjectFiles(prev => ({ ...prev, [projectId]: res.data }));
    } catch {
      setProjectFiles(prev => ({ ...prev, [projectId]: [] }));
    }
  };

  const toggleProjectFiles = (projectId) => {
    if (!expandedProjects[projectId]) {
      fetchProjectFiles(projectId);
    }
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const fetchPersonalGear = useCallback(async () => {
    try {
      const res = await personalGearAPI.getAll();
      setPersonalGear(res.data);
    } catch (error) {
      console.error('Failed to fetch personal gear:', error);
    }
  }, []);

  const handleAddGearItem = async (e) => {
    e.preventDefault();
    if (!newGearItem.trim()) return;
    setGearLoading(true);
    try {
      const res = await personalGearAPI.add(newGearItem.trim());
      setPersonalGear(prev => [...prev, res.data]);
      setNewGearItem('');
    } catch (error) {
      toast.error('Failed to add item');
    } finally {
      setGearLoading(false);
    }
  };

  const handleToggleGearItem = async (itemId) => {
    // Optimistic update
    setPersonalGear(prev => prev.map(item => 
      item.id === itemId ? { ...item, checked: !item.checked } : item
    ));
    try {
      await personalGearAPI.toggle(itemId);
    } catch (error) {
      // Revert on error
      setPersonalGear(prev => prev.map(item => 
        item.id === itemId ? { ...item, checked: !item.checked } : item
      ));
      toast.error('Failed to update item');
    }
  };

  const handleDeleteGearItem = async (itemId) => {
    setPersonalGear(prev => prev.filter(item => item.id !== itemId));
    try {
      await personalGearAPI.delete(itemId);
    } catch (error) {
      fetchPersonalGear(); // Refresh on error
      toast.error('Failed to delete item');
    }
  };

  const handleResetGearChecklist = async () => {
    if (personalGear.every(item => !item.checked)) {
      toast.info('Nothing to reset');
      return;
    }
    setPersonalGear(prev => prev.map(item => ({ ...item, checked: false })));
    try {
      await personalGearAPI.reset();
      toast.success('Checklist reset');
    } catch (error) {
      fetchPersonalGear();
      toast.error('Failed to reset');
    }
  };

  const fetchMyProfile = useCallback(async () => {
    try {
      const res = await api.get('/api/crew/my-profile');
      setMyRates(res.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  }, []);

  const handleMyPhotoUpload = async (fileObj) => {
    if (!fileObj) return;
    const fd = new FormData();
    fd.append('file', fileObj);
    try {
      await crewAPI.uploadMyPhoto(fd);
      toast.success('Photo updated');
      fetchMyProfile();
    } catch {
      toast.error('Failed to upload photo');
    }
  };

  const fetchMyEvents = useCallback(async () => {
    try {
      const res = await api.get('/api/crew/my-events');
      setEvents(res.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  }, []);

  const fetchMyAnalytics = useCallback(async () => {
    try {
      const res = await api.get('/api/crew/my-analytics');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  const handleConfirmation = async (assignmentId, status) => {
    try {
      await assignmentAPI.updateConfirmation(assignmentId, status);
      toast.success(status === 'accepted' ? 'Event accepted!' : 'Event declined');
      // Refresh data
      await Promise.all([fetchMyEvents(), fetchMyAnalytics()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleConfirmGear = async (assignmentId) => {
    try {
      await assignmentAPI.confirmGear(assignmentId);
      toast.success('Gear confirmed — thanks!');
      await fetchMyEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm gear');
    }
  };

  const fetchBlockedDates = useCallback(async () => {
    try {
      const res = await blockedDatesAPI.getAll();
      setBlockedDates(res.data);
    } catch (error) {
      console.error('Failed to fetch blocked dates:', error);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchMyEvents(), fetchMyAnalytics(), fetchBlockedDates(), fetchMyProfile(), fetchPersonalGear()]).finally(() => setLoading(false));
  }, [fetchMyEvents, fetchMyAnalytics, fetchBlockedDates, fetchMyProfile, fetchPersonalGear]);


  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatWeek = (weekStr) => {
    if (!weekStr) return weekStr;
    return `Week ${weekStr.split('-W')[1]}, ${weekStr.split('-W')[0]}`;
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const upcoming = events.filter(e => e.event.event_date >= todayStr);
  const past = events.filter(e => e.event.event_date < todayStr);

  // Check if any INR amounts exist
  const hasINR = analytics && (analytics.total_earned_inr > 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="crew-dashboard">
        <div className="mb-6 flex items-center gap-4">
          <label className="relative cursor-pointer group shrink-0" data-testid="crew-my-photo-upload">
            <Avatar name={user?.full_name} src={myRates?.photo_url} size={64} />
            <span className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <Camera size={18} className="text-white" />
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleMyPhotoUpload(e.target.files[0])} data-testid="crew-my-photo-input" />
          </label>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-1">My Schedule</h1>
            <p className="text-muted-foreground text-sm">Welcome, {user?.full_name}</p>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setMainTab('schedule')}
            className={`px-2.5 sm:px-6 py-3 text-xs sm:text-sm uppercase tracking-wide font-medium transition-colors whitespace-nowrap ${
              mainTab === 'schedule' ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-schedule"
          >
            Schedule
          </button>
          <button
            onClick={() => setMainTab('payments')}
            className={`px-2.5 sm:px-6 py-3 text-xs sm:text-sm uppercase tracking-wide font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              mainTab === 'payments' ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-payments"
          >
            <DollarSign size={14} className="hidden sm:inline" />
            Payments
          </button>
          <button
            onClick={() => setMainTab('gear')}
            className={`px-2.5 sm:px-6 py-3 text-xs sm:text-sm uppercase tracking-wide font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
              mainTab === 'gear' ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-my-gear"
          >
            <Package size={14} className="hidden sm:inline" />
            <span className="sm:hidden">Gear</span>
            <span className="hidden sm:inline">My Gear</span>
            {personalGear.length > 0 && (
              <span className="text-[10px] sm:text-xs px-1.5 py-0.5 bg-gold/10 text-gold">
                {personalGear.filter(g => g.checked).length}/{personalGear.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setMainTab('blocked')}
            className={`px-2.5 sm:px-6 py-3 text-xs sm:text-sm uppercase tracking-wide font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
              mainTab === 'blocked' ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-blocked-dates"
          >
            <CalendarOff size={14} className="hidden sm:inline" />
            <span className="sm:hidden">Blocked</span>
            <span className="hidden sm:inline">Blocked Dates</span>
            {blockedDates.length > 0 && (
              <span className="text-[10px] sm:text-xs px-1.5 py-0.5 bg-gold/10 text-gold">{blockedDates.length}</span>
            )}
          </button>
        </div>

        {mainTab === 'blocked' ? (
          /* Blocked Dates Tab */
          <div data-testid="blocked-dates-tab">
            <div className="card p-5 sm:p-6">
              <h2 className="text-xl font-serif mb-4">Your Blocked Dates</h2>
              <p className="text-sm text-muted-foreground mb-6">These are dates reserved for Ami Video. Contact admin for any changes.</p>
              {blockedDates.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No blocked dates currently</p>
              ) : (
                <div className="space-y-3">
                  {blockedDates.map(block => {
                    const isRange = block.start_date !== block.end_date;
                    const isPast = block.end_date < todayStr;
                    return (
                      <div key={block.block_id} className={`flex items-center gap-4 p-4 border ${isPast ? 'border-border opacity-50' : 'border-gold/30 bg-gold/5'}`} data-testid={`crew-block-${block.block_id}`}>
                        <CalendarOff size={18} className={isPast ? 'text-muted-foreground' : 'text-gold'} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {formatDate(block.start_date)}{isRange && ` — ${formatDate(block.end_date)}`}
                          </p>
                          {block.note && <p className="text-xs text-muted-foreground mt-0.5">{block.note}</p>}
                        </div>
                        {isPast && <span className="text-xs text-muted-foreground">Past</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : mainTab === 'payments' ? (
          /* Payments Tab - Earnings overview + event-wise payment breakdown */
          <div data-testid="payments-tab">
            {/* Summary Stats (relocated from Schedule) */}
            {analytics && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="card p-5" data-testid="stat-total-events">
                  <Calendar className="text-gold mb-2" size={20} />
                  <p className="text-3xl font-serif">{analytics.total_events}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Total Events</p>
                </div>
                <div className="card p-5" data-testid="stat-earned">
                  <DollarSign className="text-green-600 dark:text-green-400 mb-2" size={20} />
                  <p className="text-3xl font-serif">{formatCurrency(analytics.total_earned_usd, 'USD')}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Total Earned</p>
                </div>
                <div className="card p-5" data-testid="stat-pending">
                  <DollarSign className="text-amber-600 dark:text-amber-400 mb-2" size={20} />
                  <p className="text-3xl font-serif text-amber-600 dark:text-amber-400">{formatCurrency(analytics.total_pending_usd, 'USD')}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">Pending Payment</p>
                </div>
                <div className="card p-5" data-testid="stat-day-types">
                  <Clock className="text-gold mb-2" size={20} />
                  <div className="flex gap-4">
                    <div>
                      <p className="text-2xl font-serif">{analytics.full_days}</p>
                      <p className="text-xs text-muted-foreground">Full Days</p>
                    </div>
                    <div>
                      <p className="text-2xl font-serif">{analytics.half_days}</p>
                      <p className="text-xs text-muted-foreground">Half Days</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* My Rates (relocated from Schedule) */}
            {myRates && (
              <div className="card p-6 mb-6" data-testid="my-rates">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-serif">My Rates</h2>
                  <button
                    onClick={() => setShowRates(!showRates)}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-my-rates"
                  >
                    {showRates ? <EyeOff size={16} /> : <Eye size={16} />}
                    {showRates ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showRates ? (
                  <div className="grid grid-cols-2 gap-4">
                    {(myRates.half_day_rate_usd > 0 || myRates.full_day_rate_usd > 0) && (
                      <>
                        <div className="p-4 border border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Half Day (USD)</p>
                          <p className="text-xl font-serif">{formatCurrency(myRates.half_day_rate_usd, 'USD')}</p>
                        </div>
                        <div className="p-4 border border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Full Day (USD)</p>
                          <p className="text-xl font-serif">{formatCurrency(myRates.full_day_rate_usd, 'USD')}</p>
                        </div>
                      </>
                    )}
                    {(myRates.half_day_rate_inr > 0 || myRates.full_day_rate_inr > 0) && (
                      <>
                        <div className="p-4 border border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Half Day (INR)</p>
                          <p className="text-xl font-serif">{formatCurrency(myRates.half_day_rate_inr, 'INR')}</p>
                        </div>
                        <div className="p-4 border border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Full Day (INR)</p>
                          <p className="text-xl font-serif">{formatCurrency(myRates.full_day_rate_inr, 'INR')}</p>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Tap the eye icon to view your rates</p>
                )}
              </div>
            )}

            {/* My Earnings breakdown (relocated from Schedule) */}
            {analytics && (
              <div className="card p-6 mb-6" data-testid="earnings-breakdown">
                <h2 className="text-2xl font-serif mb-4">My Earnings</h2>
                <div className="flex gap-1 mb-6 border-b border-border">
                  {['weekly', 'monthly', 'yearly'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-5 py-3 text-sm uppercase tracking-wide transition-colors ${
                        activeTab === tab
                          ? 'border-b-2 border-black text-foreground font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`tab-${tab}`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                <div className="space-y-2">
                  {activeTab === 'weekly' && Object.entries(analytics.weekly).length > 0 && (
                    Object.entries(analytics.weekly).reverse().map(([week, data]) => (
                      <div key={week} className="flex items-center justify-between p-4 border border-border">
                        <div>
                          <p className="font-medium">{formatWeek(week)}</p>
                          <p className="text-xs text-muted-foreground">{data.events} event{data.events !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="text-right">
                          {data.earned_usd > 0 && (
                            <p className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(data.earned_usd, 'USD')} earned</p>
                          )}
                          {data.pending_usd > 0 && (
                            <p className="text-amber-600 dark:text-amber-400 text-sm">{formatCurrency(data.pending_usd, 'USD')} pending</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  {activeTab === 'monthly' && Object.entries(analytics.monthly).length > 0 && (
                    Object.entries(analytics.monthly).reverse().map(([month, data]) => (
                      <div key={month} className="flex items-center justify-between p-4 border border-border">
                        <div>
                          <p className="font-medium">{formatMonth(month)}</p>
                          <p className="text-xs text-muted-foreground">{data.events} event{data.events !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="text-right">
                          {data.earned_usd > 0 && (
                            <p className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(data.earned_usd, 'USD')} earned</p>
                          )}
                          {data.pending_usd > 0 && (
                            <p className="text-amber-600 dark:text-amber-400 text-sm">{formatCurrency(data.pending_usd, 'USD')} pending</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  {activeTab === 'yearly' && Object.entries(analytics.yearly).length > 0 && (
                    Object.entries(analytics.yearly).reverse().map(([year, data]) => (
                      <div key={year} className="p-5 border border-border">
                        <p className="text-xl font-serif mb-3">{year}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-serif">{data.events}</p>
                            <p className="text-xs text-muted-foreground">Events</p>
                          </div>
                          <div>
                            <p className="text-2xl font-serif text-green-600 dark:text-green-400">{formatCurrency(data.earned_usd, 'USD')}</p>
                            <p className="text-xs text-muted-foreground">Earned</p>
                          </div>
                          <div>
                            <p className="text-2xl font-serif text-amber-600 dark:text-amber-400">{formatCurrency(data.pending_usd, 'USD')}</p>
                            <p className="text-xs text-muted-foreground">Pending</p>
                          </div>
                          <div>
                            <p className="text-2xl font-serif">{formatCurrency(data.earned_usd + data.pending_usd, 'USD')}</p>
                            <p className="text-xs text-muted-foreground">Total</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  {((activeTab === 'weekly' && Object.keys(analytics.weekly).length === 0) ||
                    (activeTab === 'monthly' && Object.keys(analytics.monthly).length === 0) ||
                    (activeTab === 'yearly' && Object.keys(analytics.yearly).length === 0)) && (
                    <p className="text-muted-foreground text-center py-8">No earnings data yet</p>
                  )}
                </div>
              </div>
            )}

            <div className="card p-5 sm:p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-serif">Payment History</h2>
                  <p className="text-sm text-muted-foreground">View payment status for each event</p>
                </div>
              </div>
              
              {events.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No payment history yet</p>
              ) : (
                <div className="space-y-6">
                  {/* Group events by year */}
                  {(() => {
                    const eventsByYear = {};
                    events.forEach(item => {
                      const year = new Date(item.event.event_date).getFullYear();
                      if (!eventsByYear[year]) eventsByYear[year] = [];
                      eventsByYear[year].push(item);
                    });
                    
                    // Sort years descending (newest first)
                    const sortedYears = Object.keys(eventsByYear).sort((a, b) => b - a);
                    
                    return sortedYears.map(year => {
                      const yearEvents = eventsByYear[year].sort((a, b) => 
                        new Date(b.event.event_date) - new Date(a.event.event_date)
                      );
                      const yearPaid = yearEvents.filter(e => e.assignment.payment_status === 'paid')
                        .reduce((sum, e) => sum + (e.assignment.amount_usd || 0), 0);
                      const yearPending = yearEvents.filter(e => e.assignment.payment_status === 'pending')
                        .reduce((sum, e) => sum + (e.assignment.amount_usd || 0), 0);
                      
                      return (
                        <div key={year} className="border border-border">
                          {/* Year Header */}
                          <div className="p-4 bg-muted/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <span className="text-xl font-serif">{year}</span>
                              <span className="text-sm text-muted-foreground">
                                ({yearEvents.length} event{yearEvents.length !== 1 ? 's' : ''})
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-green-600 dark:text-green-400">{formatCurrency(yearPaid, 'USD')} paid</span>
                              <span className="text-amber-600 dark:text-amber-400">{formatCurrency(yearPending, 'USD')} pending</span>
                            </div>
                          </div>
                          
                          {/* Events in this year */}
                          <div className="divide-y divide-border">
                            {yearEvents.map(item => (
                              <div 
                                key={item.assignment.assignment_id} 
                                className={`p-4 transition-colors ${
                                  item.assignment.payment_status === 'paid' 
                                    ? 'bg-green-50/30' 
                                    : 'bg-amber-50/30'
                                }`}
                                data-testid={`payment-event-${item.assignment.assignment_id}`}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                  {/* Event Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                      <h3 className="font-medium truncate">{item.event.event_name}</h3>
                                      <span className="text-xs px-2 py-0.5 bg-gold/10 text-gold">{item.event.event_type}</span>
                                      {new Date(item.event.event_date) > new Date() && (
                                        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400">Upcoming</span>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                                      <span className="flex items-center gap-1">
                                        <Calendar size={12} />
                                        {formatDate(item.event.event_date)}
                                      </span>
                                      <span className="capitalize">{item.assignment.day_type.replace('_', ' ')}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Payment Status & Amount */}
                                  <div className="flex items-center gap-4">
                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${
                                      item.assignment.payment_status === 'paid'
                                        ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400'
                                        : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400'
                                    }`}>
                                      {item.assignment.payment_status === 'paid' 
                                        ? <><CheckCircle size={14} /> Paid</>
                                        : <><AlertCircle size={14} /> Pending</>
                                      }
                                    </div>
                                    <div className="text-right">
                                      <p className="text-lg font-medium">{formatCurrency(item.assignment.amount_usd || 0, 'USD')}</p>
                                      {item.assignment.amount_inr > 0 && (
                                        <p className="text-xs text-muted-foreground">{formatCurrency(item.assignment.amount_inr, 'INR')}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        ) : mainTab === 'gear' ? (
          /* My Gear Tab */
          <div data-testid="my-gear-tab">
            <div className="card p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-serif">My Gear Checklist</h2>
                  <p className="text-sm text-muted-foreground mt-1">Track your equipment before each event</p>
                </div>
                {personalGear.length > 0 && (
                  <button
                    onClick={handleResetGearChecklist}
                    className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    data-testid="reset-gear-btn"
                  >
                    <RotateCcw size={14} />
                    Reset All
                  </button>
                )}
              </div>

              {/* Add Item Form */}
              <form onSubmit={handleAddGearItem} className="flex gap-2 mb-6">
                <input
                  type="text"
                  value={newGearItem}
                  onChange={(e) => setNewGearItem(e.target.value)}
                  placeholder="Add equipment (e.g., Canon R5, 24-70mm lens)"
                  className="flex-1 px-4 py-3 border border-input bg-background text-sm"
                  data-testid="gear-input"
                />
                <button
                  type="submit"
                  disabled={gearLoading || !newGearItem.trim()}
                  className="flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground hover:bg-black/90 disabled:opacity-50 transition-colors text-sm uppercase tracking-wide font-medium"
                  data-testid="add-gear-btn"
                >
                  <Plus size={16} />
                  Add
                </button>
              </form>

              {/* Gear List */}
              {personalGear.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-border">
                  <Package size={40} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-2">No gear items yet</p>
                  <p className="text-xs text-muted-foreground">Add your equipment above to create a packing checklist</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {personalGear.map(item => (
                    <div
                      key={item.id}
                      className={`flex items-center gap-3 p-4 border transition-colors ${
                        item.checked ? 'border-green-200 dark:border-green-500/30 bg-green-50/50' : 'border-border hover:border-gold/30'
                      }`}
                      data-testid={`gear-item-${item.id}`}
                    >
                      <button
                        onClick={() => handleToggleGearItem(item.id)}
                        className={`w-6 h-6 flex items-center justify-center border-2 transition-colors ${
                          item.checked 
                            ? 'border-green-500 bg-green-500 text-white' 
                            : 'border-muted-foreground hover:border-gold'
                        }`}
                        data-testid={`toggle-gear-${item.id}`}
                      >
                        {item.checked && <Check size={14} />}
                      </button>
                      <span className={`flex-1 ${item.checked ? 'line-through text-muted-foreground' : ''}`}>
                        {item.name}
                      </span>
                      <button
                        onClick={() => handleDeleteGearItem(item.id)}
                        className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                        data-testid={`delete-gear-${item.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  
                  {/* Progress indicator */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Packed</span>
                      <span className="font-medium">
                        {personalGear.filter(g => g.checked).length} / {personalGear.length}
                      </span>
                    </div>
                    <div className="w-full bg-accent h-2">
                      <div 
                        className="bg-green-500 h-2 transition-all" 
                        style={{ width: `${(personalGear.filter(g => g.checked).length / personalGear.length) * 100}%` }} 
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
        /* Schedule Tab (existing content) */
        <>

        {/* Upcoming Events with Payment Status */}
        <div className="card p-6 mb-8">
          <h2 className="text-2xl font-serif mb-6">Upcoming Events</h2>
          {upcoming.length > 0 ? (
            <div className="space-y-4">
              {upcoming.map((item) => {
                const confirmStatus = item.assignment.confirmation_status || 'pending_confirmation';
                const isAccepted = confirmStatus === 'accepted';
                return (
                <div key={item.assignment.assignment_id} className={`border p-4 sm:p-5 transition-colors ${
                  isAccepted ? 'border-green-200 dark:border-green-500/30 bg-green-50/30' : 'border-border hover:border-gold/50'
                }`} data-testid={`my-event-${item.event.event_id}`}>
                  {/* Header with event name and status badges */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg sm:text-xl font-serif">{item.event.event_name}</h3>
                    <span className="inline-block px-2 py-0.5 bg-gold/10 text-gold text-xs">
                      {item.event.event_type}
                    </span>
                    {isAccepted && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 text-xs" data-testid="confirmation-accepted">
                        <Check size={12} /> Accepted
                      </span>
                    )}
                    {confirmStatus === 'pending_confirmation' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs" data-testid="confirmation-pending">
                        <AlertCircle size={12} /> Awaiting
                      </span>
                    )}
                  </div>
                  
                  {/* Couple names - only after accepting */}
                  {isAccepted && item.bride_name && item.groom_name && (
                    <p className="text-sm text-muted-foreground mb-2">{item.bride_name} & {item.groom_name}</p>
                  )}
                  
                  {/* Basic event details - always visible */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {formatDate(item.event.event_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} /> {item.event.start_time} - {item.event.end_time}
                    </span>
                    {/* Location only visible after accepting */}
                    {isAccepted && item.event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin size={14} /> {item.event.location}
                      </span>
                    )}
                  </div>
                  
                  {/* Payment info - always visible */}
                  <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border">
                    <span className="text-xs capitalize px-2 py-1 bg-muted">{item.assignment.day_type.replace('_', ' ')}</span>
                    <span className={`text-xs flex items-center gap-1 px-2 py-1 ${
                      item.assignment.payment_status === 'paid' 
                        ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400' 
                        : 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400'
                    }`}>
                      {item.assignment.payment_status === 'paid' 
                        ? <><CheckCircle size={12} /> Paid</>
                        : <><AlertCircle size={12} /> Pending</>
                      }
                    </span>
                  </div>

                  {/* Accept / Decline buttons — only when pending */}
                  {confirmStatus === 'pending_confirmation' && (
                    <div className="mt-4 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-3 italic">Accept to view full event details (location, venue, notes, etc.)</p>
                      <div className="flex gap-3" data-testid="confirmation-actions">
                        <button
                          onClick={() => handleConfirmation(item.assignment.assignment_id, 'accepted')}
                          className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white text-sm hover:bg-green-700 transition-colors"
                          data-testid="accept-assignment-btn"
                        >
                          <Check size={14} /> ACCEPT
                        </button>
                        <button
                          onClick={() => handleConfirmation(item.assignment.assignment_id, 'declined')}
                          className="flex items-center gap-2 px-5 py-2 border border-red-300 text-red-600 dark:text-red-400 text-sm hover:bg-red-50 dark:bg-red-500/10 transition-colors"
                          data-testid="decline-assignment-btn"
                        >
                          <XCircle size={14} /> DECLINE
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Gear Confirmation — hidden per admin request (backend intact) */}
                  {false && isAccepted && item.assignment.gear_checklist_sent_at && (
                    <div className="mt-4 pt-3 border-t border-border" data-testid={`gear-confirm-block-${item.assignment.assignment_id}`}>
                      {item.assignment.gear_confirmation_status === 'confirmed' ? (
                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                          <ShieldCheck size={16} />
                          <span>Gear confirmed{item.assignment.gear_confirmed_at ? ` · ${formatDate(item.assignment.gear_confirmed_at.slice(0,10))}` : ''}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                          <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                            <ClipboardCheck size={16} className="mt-0.5 flex-shrink-0" />
                            <span>Pre-event checklist sent. Please confirm your gear is packed and ready.</span>
                          </div>
                          <button
                            onClick={() => handleConfirmGear(item.assignment.assignment_id)}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm hover:bg-black/90 whitespace-nowrap"
                            data-testid={`confirm-gear-btn-${item.assignment.assignment_id}`}
                          >
                            <ShieldCheck size={14} /> I'M READY
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* === DETAILS ONLY VISIBLE AFTER ACCEPTING === */}
                  {isAccepted && (
                    <>
                      {/* Shot list (crew-side checkoff) — hidden per admin request (backend intact) */}
                      {false && <ShotListCrew eventId={item.event.event_id} />}

                      {/* Other crew on this event */}
                      {item.other_crew && item.other_crew.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Also on this event</p>
                          <div className="flex flex-wrap gap-2">
                            {item.other_crew.map((crew, idx) => (
                              <span key={idx} className="text-xs px-2 py-1 bg-muted" data-testid={`other-crew-${idx}`}>
                                {crew.name} <span className="text-muted-foreground">({crew.role})</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Project Files Section */}
                      {item.project_id && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <button
                            onClick={() => toggleProjectFiles(item.project_id)}
                            className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground hover:text-gold transition-colors"
                            data-testid={`toggle-files-${item.project_id}`}
                          >
                            <FolderOpen size={14} />
                            {expandedProjects[item.project_id] ? 'Hide' : 'View'} Project Files
                          </button>
                          {expandedProjects[item.project_id] && (
                            <div className="mt-3 space-y-2">
                              {projectFiles[item.project_id]?.length > 0 ? (
                                projectFiles[item.project_id].map(file => {
                                  const Icon = getFileIcon(file.mime_type);
                                  return (
                                    <div key={file.file_id} className="flex items-center justify-between p-2 bg-muted/50 text-sm">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <Icon size={16} className="text-gold flex-shrink-0" />
                                        <span className="truncate">{file.name}</span>
                                      </div>
                                      {file.web_view_link && (
                                        <a
                                          href={file.web_view_link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 hover:bg-accent transition-colors flex-shrink-0"
                                          title="Open file"
                                        >
                                          <ExternalLink size={14} />
                                        </a>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="text-xs text-muted-foreground">No files uploaded yet</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Get Directions link */}
                      {item.event.venue_address && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-xs text-muted-foreground mb-1">{item.event.venue_address}</p>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.event.venue_address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gold hover:underline flex items-center gap-1"
                          >
                            <MapPin size={12} /> Get Directions
                          </a>
                        </div>
                      )}

                      {/* Event notes */}
                      {item.event.event_notes && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Notes</p>
                          <p className="text-sm text-muted-foreground">{item.event.event_notes}</p>
                        </div>
                      )}

                      {/* Add to Calendar button */}
                      <div className="mt-4 pt-3 border-t border-border">
                        <a
                          href={generateCalendarUrl(item)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm hover:bg-black/90 transition-colors"
                          data-testid="add-to-calendar-btn"
                        >
                          <CalendarPlus size={16} /> Add to My Calendar
                        </a>
                      </div>
                    </>
                  )}
                </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {past.length > 0 ? 'No upcoming events — see past events below ↓' : 'No events assigned to you yet'}
            </p>
          )}
        </div>

        {/* Past Events */}
        {past.length > 0 && (
          <div className="card p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-serif mb-4 sm:mb-6" data-testid="past-events-heading">Past Events ({past.length})</h2>
            <div className="space-y-3">
              {past.map((item) => (
                <div key={item.assignment.assignment_id} className="border border-border p-3 sm:p-4" data-testid={`past-event-${item.event.event_id}`}>
                  <div className="mb-2">
                    <h3 className="font-medium text-sm sm:text-base">{item.event.event_name}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {formatDate(item.event.event_date)} &bull; {item.event.event_type}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs capitalize px-2 py-1 bg-muted">{item.assignment.day_type.replace('_', ' ')}</span>
                    <span className={`text-xs flex items-center gap-1 px-2 py-1 ${
                      item.assignment.payment_status === 'paid' 
                        ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400' 
                        : 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400'
                    }`}>
                      {item.assignment.payment_status === 'paid' 
                        ? <><CheckCircle size={12} /> Paid</>
                        : <><AlertCircle size={12} /> Pending</>
                      }
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}

        {/* Quick Actions FAB - Only show if there are pending assignments */}
        {(() => {
          const pendingAssignments = events.filter(e => 
            (e.assignment.confirmation_status || 'pending_confirmation') === 'pending_confirmation' &&
            e.event.event_date >= todayStr
          );
          
          if (pendingAssignments.length === 0) return null;
          
          return (
            <>
              {/* Floating Action Button */}
              <button
                onClick={() => setShowQuickActions(true)}
                className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-black/90 transition-all z-40 sm:hidden"
                data-testid="quick-actions-fab"
              >
                <div className="relative">
                  <Zap size={24} />
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingAssignments.length}
                  </span>
                </div>
              </button>

              {/* Quick Actions Panel */}
              {showQuickActions && (
                <div className="fixed inset-0 bg-black/50 z-50 sm:hidden" onClick={() => setShowQuickActions(false)}>
                  <div 
                    className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl max-h-[70vh] overflow-y-auto"
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div className="sticky top-0 bg-background p-4 border-b border-border flex items-center justify-between">
                      <div>
                        <h3 className="font-serif text-lg">Quick Actions</h3>
                        <p className="text-xs text-muted-foreground">{pendingAssignments.length} pending response{pendingAssignments.length > 1 ? 's' : ''}</p>
                      </div>
                      <button 
                        onClick={() => setShowQuickActions(false)}
                        className="p-2 hover:bg-accent rounded-full"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    {/* Pending Assignments List */}
                    <div className="p-4 space-y-3">
                      {pendingAssignments.map((item) => (
                        <div 
                          key={item.assignment.assignment_id} 
                          className="border border-amber-200 dark:border-amber-500/30 bg-amber-50/50 p-4 rounded-lg"
                        >
                          <div className="mb-2">
                            <h4 className="font-medium">{item.event.event_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {item.event.event_type} • {formatDate(item.event.event_date)}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <Clock size={12} /> {item.event.start_time} - {item.event.end_time}
                            </span>
                            <span className="capitalize px-2 py-0.5 bg-muted rounded">{item.assignment.day_type.replace('_', ' ')}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gold">
                              {formatCurrency(item.assignment.amount_usd, 'USD')}
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  await handleConfirmation(item.assignment.assignment_id, 'declined');
                                  if (pendingAssignments.length <= 1) setShowQuickActions(false);
                                }}
                                className="flex items-center gap-1.5 px-4 py-2 bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400 rounded-lg text-sm font-medium"
                              >
                                <XCircle size={16} /> Decline
                              </button>
                              <button
                                onClick={async () => {
                                  await handleConfirmation(item.assignment.assignment_id, 'accepted');
                                  if (pendingAssignments.length <= 1) setShowQuickActions(false);
                                }}
                                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium"
                              >
                                <Check size={16} /> Accept
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </Layout>
  );
};

export default CrewDashboard;
